/**
 * CMS roles for the KBI Admin (docs/kbi.md section 1).
 *
 * An unknown role (`null`: an old session without a stored employee) is
 * treated permissively so existing pages keep working; the backend still
 * answers 403 where it matters.
 */
export type TRole = string | null | undefined;

const OPERATE = ['super_admin', 'operation_admin', 'kbi_operator'];
const VALIDATE = ['super_admin', 'kbi_validator'];
// Only an admin draws the validators' sample.
const DRAW_SAMPLE = ['super_admin'];
// kbi_validator only works in the Validator workspace, narrower than docs/kbi.md.
const DASHBOARD = OPERATE;
const AGENT_VERSIONS = [...DASHBOARD, 'airline_admin'];

/** Roles that only see KBI pages. */
const KBI_ONLY = ['kbi_operator', 'kbi_validator'];

const allow = (list: string[]) => (role: TRole) =>
  !role || list.includes(role);

/** Setup, operations and baseline admin. */
export const canOperate = allow(OPERATE);
/** Dashboard (read). */
export const canViewDashboard = allow(DASHBOARD);
/** Validator review. */
export const canValidate = allow(VALIDATE);
/** Draw or extend the validators' sample. */
export const canDrawSample = allow(DRAW_SAMPLE);
export const canViewAgentVersions = allow(AGENT_VERSIONS);
/** Flight simulator: the admin API answers SUPER_ADMIN only. */
export const canSimulate = (role: TRole): boolean =>
  !role || role === 'super_admin';

/** kbi_operator / kbi_validator: land on and see KBI pages only. */
export const isKbiOnlyRole = (role: TRole): boolean =>
  !!role && KBI_ONLY.includes(role);

/** Employees admin: every role but the KBI-only ones. */
export const canManageEmployees = (role: TRole): boolean =>
  !isKbiOnlyRole(role);

/** Where `/` goes for this role. */
export const getHomePath = (role: TRole): string =>
  role === 'kbi_validator'
    ? '/kbi/validator'
    : isKbiOnlyRole(role)
    ? '/kbi'
    : '/onboarding-admin';
